import type { Show } from '../types/show';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

interface DetailedRating {
  stats: [
    one: number,    // 1星评分比例
    two: number,    // 2星评分比例
    three: number,  // 3星评分比例
    four: number,   // 4星评分比例
    five: number    // 5星评分比例
  ];               // 评分比例总和为1
  doing_count: number;    // 在看人数
  wish_count: number;     // 想看人数
  done_count: number;     // 看过人数
  type_ranks: {
    type: string;         // 类型
    rank: number;         // 类型占比 就是%多少的类型
  }[];
  calculated_stats: {     // 计算得出的统计数据
    score: number;        // 计算得分
    totalCount: number;   // 总人数
    distribution: {       // 计算后的人数分布
      one: number;
      two: number;
      three: number;
      four: number;
      five: number;
    };
    formatted: string;    // 格式化的统计信息
  };
}

interface RatingStats {
  score: number;          // 最终得分
  totalCount: number;     // 总人数
  distribution: {         // 各星级人数分布
    one: number;
    two: number;
    three: number;
    four: number;
    five: number;
  };
}

/**
 * 计算评分统计数据
 * @param stats 五星比例数组 [1星比例, 2星比例, 3星比例, 4星比例, 5星比例]
 * @param targetCount 目标总人数
 * @returns 计算结果，包含得分和人数分布
 */
export function calculateRatingStats(stats: number[], targetCount: number): RatingStats {
  const ERROR_THRESHOLD = 1e-6; // 误差阈值，对应易语言中的 1e-006

  // 从1开始到最大人数循环，查找符合条件的总人数
  for (let totalCount = 1; totalCount <= targetCount; totalCount++) {
    // 计算每个星级的人数
    const count1 = totalCount * stats[0]; // 1星人数
    const count2 = totalCount * stats[1]; // 2星人数  
    const count3 = totalCount * stats[2]; // 3星人数
    const count4 = totalCount * stats[3]; // 4星人数
    const count5 = totalCount * stats[4]; // 5星人数

    // 计算误差（对应易语言中的误差计算逻辑）
    const error1 = Math.abs(count1 - Math.round(count1)) / 100;
    const error2 = Math.abs(count2 - Math.round(count2)) / 100;
    const error3 = Math.abs(count3 - Math.round(count3)) / 100;
    const error4 = Math.abs(count4 - Math.round(count4)) / 100;
    const error5 = Math.abs(count5 - Math.round(count5)) / 100;

    // 检查所有条件（对应易语言中的条件判断）
    const errorsValid = error1 < ERROR_THRESHOLD &&
      error2 < ERROR_THRESHOLD &&
      error3 < ERROR_THRESHOLD &&
      error4 < ERROR_THRESHOLD &&
      error5 < ERROR_THRESHOLD;

    const integersValid = Math.abs(Math.floor(count1) - count1) < 0.1 &&
      Math.abs(Math.floor(count2) - count2) < 0.1 &&
      Math.abs(Math.floor(count3) - count3) < 0.1 &&
      Math.abs(Math.floor(count4) - count4) < 0.1 &&
      Math.abs(Math.floor(count5) - count5) < 0.1;

    if (errorsValid && integersValid) {
      // 找到符合条件的总人数，计算最终得分
      const score = (count1 / totalCount * 2 +
        count2 / totalCount * 4 +
        count3 / totalCount * 6 +
        count4 / totalCount * 8 +
        count5 / totalCount * 10);

      return {
        score,
        totalCount,
        distribution: {
          one: Math.round(count1),
          two: Math.round(count2),
          three: Math.round(count3),
          four: Math.round(count4),
          five: Math.round(count5)
        }
      };
    }
  }

  // 如果没有找到符合条件的值，使用目标人数计算（兜底逻辑）
  const count1 = targetCount * stats[0];
  const count2 = targetCount * stats[1];
  const count3 = targetCount * stats[2];
  const count4 = targetCount * stats[3];
  const count5 = targetCount * stats[4];

  const score = (count1 / targetCount * 2 +
    count2 / targetCount * 4 +
    count3 / targetCount * 6 +
    count4 / targetCount * 8 +
    count5 / targetCount * 10);

  return {
    score,
    totalCount: targetCount,
    distribution: {
      one: Math.round(count1),
      two: Math.round(count2),
      three: Math.round(count3),
      four: Math.round(count4),
      five: Math.round(count5)
    }
  };
}

/**
 * 格式化评分统计数据为文本
 * @param stats 评分统计数据
 * @returns 格式化后的文本
 */
export function formatRatingStats(stats: RatingStats): string {
  const { score, totalCount, distribution } = stats;
  const { one, two, three, four, five } = distribution;

  return [
    `得分:---------->${score}`,
    `总人数:${totalCount}`,
    `1-5星:${one}|${two}|${three}|${four}|${five}`,
    `5-1星:${five}|${four}|${three}|${two}|${one}`
  ].join('\n');
}

export async function getShows(): Promise<Show[]> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/shows`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Failed to fetch shows:', error);
    throw error;
  }
}

export async function getDetailedRating(id: string): Promise<DetailedRating> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/rating/subject/${id}`);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    // 计算评分统计数据
    // 对于电影使用 done_count，对于剧集使用 doing_count
    const targetCount = data.type_ranks?.[0]?.type.includes('片') ? data.done_count : data.doing_count;
    const ratingStats = calculateRatingStats(data.stats, targetCount);

    return {
      stats: data.stats,
      doing_count: data.doing_count,
      wish_count: data.wish_count,
      done_count: data.done_count,
      type_ranks: data.type_ranks || [],
      calculated_stats: {
        ...ratingStats,
        formatted: formatRatingStats(ratingStats)
      }
    };
  } catch (error) {
    console.error('Failed to fetch detailed rating:', error);
    throw error;
  }
}

export async function getRatingHistory(id: string) {
  const response = await fetch(`${API_BASE_URL}/api/rating/history/${id}`);
  if (!response.ok) {
    throw new Error(`Failed to fetch rating history: ${response.status}`);
  }
  const data = await response.json();

  // 处理历史记录中的每个条目
  if (!data.history || !Array.isArray(data.history)) {
    console.error('Invalid history data structure:', data);
    return { history: [] };
  }

  // 简单验证并返回原始历史数据，不进行重新计算
  const validHistory = data.history.filter((item: any, index: number) => {
    // 基本数据验证
    if (!item.fetch_time) {
      console.warn('[getRatingHistory] Skipping item without fetch_time:', { index });
      return false;
    }

    if (!Array.isArray(item.stats) || item.stats.length !== 5) {
      console.warn('[getRatingHistory] Skipping item with invalid stats:', {
        index,
        stats: item.stats,
        fetch_time: item.fetch_time
      });
      return false;
    }

    if (!item.doing_count && !item.done_count) {
      console.warn('[getRatingHistory] Skipping item with no count data:', {
        index,
        fetch_time: item.fetch_time
      });
      return false;
    }

    return true;
  });

  console.log('[getRatingHistory] History summary:', {
    originalCount: data.history.length,
    validCount: validHistory.length,
    skippedCount: data.history.length - validHistory.length
  });

  return { history: validHistory };
}

// 批量更新相关接口
export interface UpdateStatus {
  total_shows: number;
  never_updated: number;
  outdated: number;
  recent_updated: number;
  pending_update: number;
  recent_updates: Array<{
    id: string;
    title: string;
    last_updated: string;
  }>;
  update_recommended: boolean;
  message: string;
}

export interface BatchUpdateResult {
  success: boolean;
  processed: number;
  errors: number;
  rated: number;  // 新增：发现有评分的数量
  total_shows: number;
  remaining: number;
  execution_time_ms: number;
  results: Array<{
    id: string;
    title: string;
    status: 'success' | 'api_error' | 'error';
    hasRating?: boolean;
    error?: string;
  }>;
  next_batch_recommended: boolean;
  message: string;
}

export async function getUpdateStatus(): Promise<UpdateStatus> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/update-status`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Failed to fetch update status:', error);
    throw error;
  }
}

export async function runBatchUpdate(batchSize: number = 10): Promise<BatchUpdateResult> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/batch-update?batch_size=${batchSize}`, {
      method: 'GET',
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Failed to run batch update:', error);
    throw error;
  }
} 